import { getEmployeePayrollId } from '@nxsys/utils/employee-payroll-id';
import AppButton from '@nxsys/components/AppButton';
import { useDrawerSave } from './useDrawerSave';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppLoader from '@nxsys/components/AppLoader';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppSwitch from '@nxsys/components/AppSwitch';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import {
  defaultPaymentFrequency,
  defaultRightToWorkStatus,
  regulationOptions,
} from '@nxsys/constants/AppConstants';
import { getRightToWorkStatusOptions } from '@nxsys/utils/rightToWorkOptions';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { UMBRELLA_PAYE } from '@nxsys/constants/AppConst';
import {
  useUpdateEmployeeById,
  useUpdateSettingEmployeeById,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import {
  UpdateEmployeeByIdParams,
  UpdateSettingEmployeeByIdParams,
} from '@nxsys/types/api';
import { Icons } from '@nxsys/types/icons';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Col, Divider, Form, Row, Tooltip, Typography } from 'antd';
import { includes, toNumber } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { IRELAND } from '@nxsys/store/constants/keys';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import { useEmployerFieldLabels } from '@nxsys/hooks/useEmployerFieldLabels';
import WorkingPatternFields from '../components/WorkingPatternFields';
import {
  buildWorkingPatternUpdateParams,
  findDefaultWorkingPattern,
  getWorkingPatternInitialValues,
} from '../components/workingPatternUtils';

type Props = {
  data: EmployeeRecordType | undefined;
  onRefetch?: () => void;
};

const Employment = ({ data, onRefetch }: Props) => {
  const { messages } = useTypedIntl();
  const employerLabels = useEmployerFieldLabels();
  const dispatch = useDispatch<AppDispatch>();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const currencySymbol = showCurrencyByCountry(payrollCountry);
  const isUmbrellaPaye = data
    ? includes(UMBRELLA_PAYE, data.setting?.paymentModel)
    : false;
  const isCisNonCis = data
    ? includes(
        [PAYMENT_MODEL_ENUM.CIS, PAYMENT_MODEL_ENUM.NON_CIS],
        data.setting?.paymentModel,
      )
    : false;

  const [form] = Form.useForm();
  const watchValues = Form.useWatch([], form);
  const [showStarterDetails, setShowStarterDetails] = useState(false);
  const { run: updateEmployee, loading: isUpdatingEmployee } =
    useUpdateEmployeeById();
  const { run: updateSettingEmployeeById, loading: isUpdatingSettingEmployee } =
    useUpdateSettingEmployeeById();

  useEffect(() => {
    if (data) {
      const s = data.setting;
      form.setFieldsValue({
        // NX-4948: working pattern + payroll calculation dates (pro-rata)
        ...getWorkingPatternInitialValues(data),
        // General
        paymentCompany: data.companyPayment
          ? {
              label: data.companyPayment.companyName,
              value: data.companyPayment.id,
            }
          : null,
        employmentId: s?.employmentId ?? '',
        employmentStartDate: data.dateOfJoining
          ? moment(data.dateOfJoining)
          : null,
        payFrequency: data.payFrequency
          ? (defaultPaymentFrequency.find(
              (item) => item.value === data.payFrequency,
            ) ?? null)
          : null,
        annualSalary: data.annualSalary ?? '',
        workingTimeRegulation: data.workingTimeRegulation
          ? (regulationOptions.find(
              (item) => item.value === data.workingTimeRegulation,
            ) ?? null)
          : null,
        hourlyRate: data.hourlyRate ?? data.rateHour ?? '',
        // CIS/Non-CIS: Right to Work
        rightToWorkStatus: data.rightToWorkStatus
          ? (defaultRightToWorkStatus.find(
              (item) => item.value === data.rightToWorkStatus,
            ) ?? null)
          : null,
        documentExpiryDate: data.documentExpiryDate
          ? moment(data.documentExpiryDate)
          : null,
        document: data.document ?? '',
        isRestrictedHours: data.isRestrictedHours ?? false,
        totalPayLiableToTax: s.totalPayLiableToTax
          ? toNumber(s.totalPayLiableToTax)
          : null,
        totalPayLiableToASC: s.totalPayLiableToASC
          ? toNumber(s.totalPayLiableToASC)
          : null,
        totalPayLiableToUSC: s.totalPayLiableToUSC
          ? toNumber(s.totalPayLiableToUSC)
          : null,
        totalTax: s.totalTax ? toNumber(s.totalTax) : null,
        totalUSC: s.totalUSC ? toNumber(s.totalUSC) : null,
        totalASC: s.totalASC ? toNumber(s.totalASC) : null,
      });
      setShowStarterDetails(
        !!(
          s.totalPayLiableToTax ||
          s.totalPayLiableToASC ||
          s.totalPayLiableToUSC ||
          s.totalTax ||
          s.totalUSC ||
          s.totalASC
        ),
      );
    }
  }, [data]);

  const isSaving = isUpdatingEmployee || isUpdatingSettingEmployee;

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!data?.id) return;

      // API 1: PUT /subcontractor/:id - general employee fields
      const employeeParams: Partial<UpdateEmployeeByIdParams> = {
        companyPayment: values.paymentCompany?.value || null,
        dateOfJoining: values.employmentStartDate
          ? moment(values.employmentStartDate).toISOString()
          : '',
        payFrequency: values.payFrequency?.value || null,
        workingTimeRegulation: values.workingTimeRegulation?.value || null,
        annualSalary: values.annualSalary ?? '',
        rateHour: values.hourlyRate ?? '',
        mobile: data.mobile || null,
        ...buildWorkingPatternUpdateParams(values, data),
      };

      // CIS/Non-CIS: add Right to Work fields
      if (isCisNonCis) {
        employeeParams.rightToWorkStatus =
          values.rightToWorkStatus?.value || null;
        employeeParams.documentExpiryDate = values.documentExpiryDate
          ? moment(values.documentExpiryDate).toISOString()
          : null;
        employeeParams.document = data.document || null;
        employeeParams.isRestrictedHours = values.isRestrictedHours ?? false;

        const isUploadFile = !!(values.document as File)?.name;
        if (isUploadFile) {
          const res = await dispatch(
            commonActions.uploadFileToS3({ file: values.document as File }),
          ).unwrap();
          employeeParams.document = res.url;
        }
      }

      await updateEmployee(data.id, employeeParams as UpdateEmployeeByIdParams);

      const params = {
        totalPayLiableToTax: values.totalPayLiableToTax
          ? toNumber(values.totalPayLiableToTax)
          : null,
        totalPayLiableToASC: values.totalPayLiableToASC
          ? toNumber(values.totalPayLiableToASC)
          : null,
        totalPayLiableToUSC: values.totalPayLiableToUSC
          ? toNumber(values.totalPayLiableToUSC)
          : null,
        totalTax: values.totalTax ? toNumber(values.totalTax) : null,
        totalUSC: values.totalUSC ? toNumber(values.totalUSC) : null,
        totalASC: values.totalASC ? toNumber(values.totalASC) : null,
      };

      await updateSettingEmployeeById(
        data.id,
        params as UpdateSettingEmployeeByIdParams,
      );

      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      onRefetch?.();
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
    }
  };

  // NX-4682: in the quick-edit drawer, surface Save in the drawer header.
  const inDrawer = useDrawerSave(handleSave, isSaving);

  if (!data)
    return (
      <div className="relative h-[100px]">
        <AppLoader />
      </div>
    );

  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-8 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.employment'] ?? 'Employment'}
      </Typography.Title>

      <AppFormContainer
        form={form}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        {/* ===== General Section ===== */}

        {/* Payment Company / Contractor */}
        <Form.Item
          label={
            isCisNonCis
              ? (messages['common.contractor'] ?? 'Contractor')
              : employerLabels.label
          }
          name="paymentCompany"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <AppSelectSync
            type="company"
            apiType="searchCompany"
            className="w-full !min-w-0"
            placeholder={messages['form.select']}
            onChangeValue={(value: any) => {
              form.setFieldValue('paymentCompany', value);
            }}
          />
        </Form.Item>

        {/* Employment ID (Ireland only) | Employment Start Date */}
        <Row gutter={24}>
          {isIreland && (
            <Col xs={24} sm={12}>
              <Form.Item
                label={messages['common.employmentId']}
                name="employmentId"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.employmentStartDate']}
              name="employmentStartDate"
              rules={[
                { required: true, message: messages['form.pleaseSelect'] },
              ]}
            >
              <AppDatePicker
                placeholder={messages['form.selectDate']}
                format={'DD/MM/YYYY'}
              />
            </Form.Item>
          </Col>
          {/* NX-5705: current payroll ID (works number reported to HMRC on RTI) */}
          {!isIreland && (
            <Col xs={24} sm={12}>
              <Form.Item label={messages['common.payrollNo'] ?? 'Payroll No'}>
                <AppInput value={getEmployeePayrollId(data)} disabled />
              </Form.Item>
            </Col>
          )}
        </Row>

        {/* Pay Frequency */}
        <Form.Item
          label={messages['common.payFrequency']}
          name="payFrequency"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <AppSelect
            options={defaultPaymentFrequency}
            className="w-full !min-w-0"
            placeholder={messages['form.select']}
          />
        </Form.Item>

        {/* Working Time Regulation */}
        {/* NX-5228: the Working Time Regulations are UK legislation. Ireland
            has the Organisation of Working Time Act, tracked separately. */}
        {!isUmbrellaPaye && !isCisNonCis && !isIreland && (
          <Form.Item
            label={messages['common.workingTimeRegulation']}
            name="workingTimeRegulation"
          >
            <AppSelect
              options={regulationOptions}
              className="w-full !min-w-0"
              placeholder={messages['form.select']}
            />
          </Form.Item>
        )}

        {/* Annual Salary | Hourly Rate */}
        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.annualSalary']}
              name="annualSalary"
            >
              <AppCurrencyInput
                prefix={currencySymbol}
                placeholder={messages['form.enter']}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.hourlyRate'] ?? 'Hourly Rate'}
              name="hourlyRate"
            >
              <AppCurrencyInput
                prefix={currencySymbol}
                placeholder={messages['form.enter']}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* NX-4948: Working Pattern (Old UI parity) */}
        <Divider />
        <WorkingPatternFields
          form={form}
          companyId={watchValues?.paymentCompany?.value}
          // A newly picked company carries its record (company search joins
          // the default pattern); the saved one only has { label, value }.
          companyDefaultPattern={findDefaultWorkingPattern(
            watchValues?.paymentCompany?.fullData?.salaryWorkingPatterns ??
              data.companyPayment?.salaryWorkingPatterns,
          )}
          dateOfJoining={watchValues?.employmentStartDate}
          stored={{
            companyId: data.companyPayment?.id,
            values: getWorkingPatternInitialValues(data),
          }}
        />

        {/* ===== CIS/Non-CIS: Right to Work Section ===== */}
        {isCisNonCis && (
          <>
            <Divider />
            <Typography.Title
              level={5}
              className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
            >
              {messages['common.rightToWork']}
            </Typography.Title>

            <Form.Item
              label={messages['common.status']}
              name="rightToWorkStatus"
            >
              <AppSelect
                options={getRightToWorkStatusOptions(
                  defaultRightToWorkStatus,
                  payrollCountry,
                  data?.rightToWorkStatus,
                )}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
              />
            </Form.Item>

            <Form.Item
              label={messages['common.documentExpiryDate']}
              name="documentExpiryDate"
            >
              <AppDatePicker
                placeholder={messages['form.selectDate']}
                format={'DD/MM/YYYY'}
              />
            </Form.Item>

            <Form.Item
              label={messages['common.attachDocument'] ?? 'Attach Document'}
              name="document"
            >
              <AppUploadFile
                urlFile={
                  typeof watchValues?.document === 'string'
                    ? watchValues?.document
                    : undefined
                }
                onChange={(file: File) => {
                  form.setFieldsValue({ document: file });
                }}
              />
            </Form.Item>

            {/* Restricted Hours — NX-5228: the 20-hour restricted-hours check only runs for UK payrolls
               (getIsRestrictedHoursValidation, Tier 4 student visa only). */}
            {!isIreland && (
              <div className="flex items-center gap-3 mb-6">
                <span className="text-sm font-medium">
                  {messages['common.restrictedHours']}?
                </span>
                <Tooltip
                  title={
                    messages['common.restrictedHoursTooltip'] ??
                    'If enabled it will flag if employee works over restricted hours'
                  }
                >
                  <InfoCircleOutlined className="cursor-pointer" />
                </Tooltip>
                <Form.Item
                  name="isRestrictedHours"
                  valuePropName="checked"
                  className="!mb-0"
                >
                  <AppSwitch />
                </Form.Item>
              </div>
            )}
          </>
        )}

        {/* ===== Ireland only ===== */}
        {isIreland && (
          <>
            <div className="h-[8px] w-full bg-neutral-100 rounded-full mb-6" />

            {/* Starter Details Button */}
            <AppButton
              variant="outlined"
              leftIcon={
                <Icons.IconFilePlus color={defaultTheme.theme.palette.brand} />
              }
              name={messages['common.starterDetails'] ?? 'Starter Details'}
              className="mb-6"
              onClick={() => setShowStarterDetails(!showStarterDetails)}
            />

            {/* ===== Starter Details Sections (toggle) ===== */}
            {showStarterDetails && (
              <>
                {/* Year to Date Figures */}
                <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-6" />
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.totalPayLiableToTax']}
                      name="totalPayLiableToTax"
                    >
                      <AppCurrencyInput
                        prefix={currencySymbol}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.totalTax']}
                      name="totalTax"
                    >
                      <AppCurrencyInput
                        prefix={currencySymbol}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.totalPayLiableToUSC']}
                      name="totalPayLiableToUSC"
                    >
                      <AppCurrencyInput
                        prefix={currencySymbol}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.totalUSC']}
                      name="totalUSC"
                    >
                      <AppCurrencyInput
                        prefix={currencySymbol}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.totalPayLiableToASC']}
                      name="totalPayLiableToASC"
                    >
                      <AppCurrencyInput
                        prefix={currencySymbol}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.totalASC']}
                      name="totalASC"
                    >
                      <AppCurrencyInput
                        prefix={currencySymbol}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </>
        )}

        {/* Action Buttons */}
        {!inDrawer && (
          <div className="flex items-center justify-start gap-3 pt-6">
            <AppButton
              type="primary"
              name={messages['common.save']}
              leftIcon={
                <Icons.IconCheckCircle
                  color={defaultTheme.theme.palette.white}
                />
              }
              onClick={handleSave}
              loading={isSaving}
            />
          </div>
        )}
      </AppFormContainer>
    </div>
  );
};

export default Employment;
